import React, { useEffect, useMemo, useState } from 'react';
import cl from 'classnames';
import { useParams } from 'react-router-dom';
import { HeadsetEvent } from 'src/models';
import { HEADSETS } from 'src/constants';
import { formatDatetime } from 'src/helper';
import { useContextData, useLanguage, usePage } from 'src/hooks';
import { useAppDispatch } from 'src/store';
import {
  useHeadsetById,
  fetchHeadsetEventsAction,
} from 'src/services/headsets';
import {
  PageSwitcher,
  StickyTable,
  StickyTableConfig,
  useStickyCols,
} from 'src/components';
import {
  FilterModal,
  FilterModalButton,
  FilterModalIndicator,
  Tooltip,
  useDateRangePicker,
} from 'src/components/general';
import styles from './headsetEventsStyle.module.scss';

export const HeadsetEvents = () => {
  const { hotel: companyId, headset: headsetId } = useParams() as {
    hotel: string;
    headset: string;
  };
  const {
    header: { width: headerWidth },
  } = useContextData();
  const {
    access: { accessMap },
  } = usePage();
  const dispatch = useAppDispatch();
  const { t, currentLang } = useLanguage();
  const headset = useHeadsetById(headsetId);
  const [isLoading, setIsLoading] = useState<null | boolean>(null);
  const [isFiltered, setIsFiltered] = useState(false);
  const [showFiltersModal, setShowFiltersModal] = useState(false);

  const events = useMemo(() => headset?.events || [], [headset?.events]);

  const {
    range,
    initRange,
    dateRangePicker,
    modalDateRangePicker,
    applyDRPChanges,
    resetDRPChanges,
    resetDRPToInit,
  } = useDateRangePicker({
    inputClassName: styles.datepickerInput,
    modalInputClassName: styles.datepickerModalInput,
    withoutClear: true,
    disabled: isLoading,
  });

  const rangeIndicatorText = useMemo(
    () =>
      currentLang && range.start && range.end ? (
        <>
          <span className={styles.nowrap}>{range.start.localString}</span>
          {' - '}
          <span className={styles.nowrap}>{range.end.localString}</span>
        </>
      ) : undefined,
    [range.end, range.start, currentLang],
  );

  const headsetIsLoaded = useMemo(() => Boolean(headset), [headset]);
  const isRangeChanged = useMemo(
    () =>
      range.start?.time !== initRange.start?.time ||
      range.end?.time !== initRange.end?.time,
    [
      initRange.start?.time,
      initRange.end?.time,
      range.start?.time,
      range.end?.time,
    ],
  );

  useEffect(() => {
    if (!headsetIsLoaded) {
      return;
    }

    setIsLoading(true);
    setIsFiltered(isRangeChanged);

    let start: string | undefined = undefined;
    let end: string | undefined = undefined;

    if (range.start && range.end) {
      start = range.start.format;
      end = range.end.format;
    }

    dispatch(
      fetchHeadsetEventsAction({
        companyId,
        headsetId,
        params: {
          start,
          end,
        },
      }),
    ).then(() => {
      setIsLoading(false);
    });
  }, [dispatch, headsetIsLoaded, headsetId, companyId, range, isRangeChanged]);

  const config: StickyTableConfig<HeadsetEvent>[] = useMemo(
    () => [
      {
        title: t('page.headset.events.eventType'),
        key: 'eventType',
        field: 'eventType',
        render: (value, event) => (
          <Tooltip
            text={`ID: ${event.id}`}
            tooltipClassName={styles.tooltip}
            arrowClassName={styles.tooltipArrow}
            withClipboard
          >
            {value}
          </Tooltip>
        ),
      },
      {
        title: t('page.headset.events.firmware'),
        key: 'firmware',
        field: 'firmware',
      },
      {
        title: t('page.headset.events.createAt'),
        key: 'createAt',
        field: 'createAt',
        render: (value) => (
          <div className={styles.tag}>{formatDatetime(value)}</div>
        ),
      },
      {
        title: t('page.headset.events.metaData'),
        key: 'metaData',
        field: 'metaData',
        render: (value) => JSON.stringify(value, undefined, 4),
        expandLabel: t('page.headset.events.showMeta'),
      },
    ],
    [t],
  );

  const {
    stickyCols,
    stickyColsFilter,
    modalStickyColsFilter,
    applySCChanges,
    resetSCChanges,
  } = useStickyCols({
    config,
    className: styles.stickyCols,
    modalClassName: styles.modalStickyCols,
  });

  return (
    <div className={styles.container}>
      <FilterModal
        show={showFiltersModal}
        close={() => {
          setShowFiltersModal(false);
        }}
        onApply={() => {
          applySCChanges();
          applyDRPChanges();
          setShowFiltersModal(false);
        }}
        onCancel={() => {
          resetSCChanges();
          resetDRPChanges();
        }}
      >
        {modalDateRangePicker}
        {modalStickyColsFilter}
      </FilterModal>
      <div
        className={styles.topSection}
        style={{ minWidth: headerWidth, maxWidth: headerWidth }}
      >
        <div
          className={cl(
            styles.pageSwitcherContainer,
            accessMap[HEADSETS].length === 1 && styles.inactive,
          )}
        >
          <PageSwitcher
            className={styles.pageSwitcher}
            selectClassName={styles.mobileSelect}
          />
          <div className={styles.filterButtons}>
            <FilterModalButton
              setShow={setShowFiltersModal}
              disabled={isLoading}
            />
            <FilterModalIndicator
              icon="calendar"
              text={rangeIndicatorText}
              disabled={isLoading}
            />
          </div>
        </div>
        <div className={styles.filters}>
          {dateRangePicker}
          {stickyColsFilter}
        </div>
      </div>
      <StickyTable
        config={config}
        data={events}
        withPaginator
        isLoading={isLoading}
        stickyCols={stickyCols}
        className={styles.table}
        isFiltered={isFiltered}
        resetFilter={resetDRPToInit}
        notFoundText={t('general.resetFilters')}
      />
    </div>
  );
};

export default HeadsetEvents;
